import { ApolloLink } from '@apollo/client';
import DataLoader from 'dataloader';
import { asyncMap } from '@apollo/client/utilities';
import { aidtKerisAPI } from '@ui/components/aidt/utils/aidtKerisAPI';
import { isAIDTUser } from '@ui/components/aidt/utils/isAIDTUser';
import { API_CODES } from '@ui/components/aidt/utils/type';

function createLoaders() {
  return {
    userNames: new DataLoader(async (ids: readonly string[]) => {
      try {
        if (ids.length === 0) return [];
        const response = await aidtKerisAPI.getUserNames(ids as string[]);

        return response;
      } catch (e) {
        return [];
      }
      return [];
    }),
    mySchoolName: new DataLoader(async (orgIds: readonly string[]) => {
      try {
        const res = await aidtKerisAPI.getMySchoolName();
        if (!res) {
          return '';
        }
        if (res.code === API_CODES.OK) {
          return orgIds.map(() => res.school_name);
        }

        return '';
      } catch (e) {
        return '';
      }
    }),
    groupNames: new DataLoader(async (ids: readonly string[]) => {
      try {
        const response = await aidtKerisAPI.getLectureCodeGroupNames();
        return ids.map(id => response.find(r => r.groupId === id)?.groupName ?? '');
      } catch (e) {
        return [];
      }
    }),
  };
}

const loaders = createLoaders();

const customResolvers = {
  Group: {
    name: async (group, _, { loaders }) => {
      try {
        const groupName = await loaders.groupNames.load(group.id);
        return groupName ?? group.name;
      } catch (e) {
        return group.name;
      }
    },
  },
  Organization: {
    name: async (organization, _, { loaders }) => {
      try {
        const mySchoolName = await loaders.mySchoolName.load(organization.id);
        return mySchoolName ?? organization.name;
      } catch (e) {
        return organization.name;
      }
    },
  },
  User: {
    name: async (user, _, { loaders }) => {
      try {
        const userName = await loaders.userNames.load(user.id);

        return userName;
      } catch (e) {
        return user.name;
      }
    },
  },
  StudentCustomStudyUnitCommentDetail: {
    text: async (studentCustomStudyUnitCommentDetail, _, { loaders }) => {
      try {
        if (studentCustomStudyUnitCommentDetail.type === 'STUDENT_LINK') {
          const userName = await loaders.userNames.load(studentCustomStudyUnitCommentDetail.studentId);
          return userName ?? '-';
        }

        return studentCustomStudyUnitCommentDetail.text;
      } catch (e) {
        return studentCustomStudyUnitCommentDetail.text;
      }
    },
  },
  RecommendationComment: {
    text: async (recommendationComment, _, { loaders }) => {
      try {
        if (recommendationComment.type === 'STUDENT_LINK') {
          const userName = await loaders.userNames.load(recommendationComment.studentId);
          return userName ?? '-';
        }
        return recommendationComment.text;
      } catch (e) {
        return recommendationComment.text;
      }
    },
  },
};

async function modifyUserNameInResponse(data: any) {
  if (Array.isArray(data)) {
    return Promise.all(data.map((item) => modifyUserNameInResponse(item)));
  } else if (typeof data === 'object' && data !== null) {
    const promises: Promise<void>[] = [];

    if (data.__typename === 'User' && 'name' in data) {
      promises.push(
        customResolvers.User.name(data, null, { loaders }).then((userName) => {
          data.name = userName ?? data.name;
        }),
      );
    } else if (data.__typename === 'StudentCustomStudyUnitCommentDetail' && 'text' in data) {
      promises.push(
        customResolvers.StudentCustomStudyUnitCommentDetail.text(data, null, { loaders }).then((text) => {
          data.text = text ?? data.text;
        }),
      );
    } else if (data.__typename === 'RecommendationComment' && 'text' in data) {
      promises.push(
        customResolvers.RecommendationComment.text(data, null, { loaders }).then((text) => {
          data.text = text ?? data.text;
        }),
      );
    } else if (data.__typename === 'Organization' && 'name' in data) {
      promises.push(
        customResolvers.Organization.name(data, null, { loaders }).then((name) => {
          data.name = name ?? data.name;
        }),
      );
    } else if (data.__typename === 'Group' && 'name' in data) {
      promises.push(
        customResolvers.Group.name(data, null, { loaders }).then((name) => {
          if (name) {
            data.name = `${data.name} - ${name}`;
          }
        }),
      );
    }

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        promises.push(
          modifyUserNameInResponse(data[key]).then((modified) => {
            data[key] = modified;
          }),
        );
      }
    }

    await Promise.all(promises);
  }

  return data;
}

// @ts-ignore
export const kerisPersonalDataLink = new ApolloLink((operation, forward) => {
  return asyncMap(forward(operation), async (response) => {
    try {
      if (!isAIDTUser()) {
        return response;
      }

      await modifyUserNameInResponse(response);

      return response;
    } catch (e) {
      return response;
    }
  });
});
